import React from 'react';

/**
 * Party Scroll v6 — the status bar that sits above a card's poster.
 *
 * The bar IS the headline (design rule 3): a red hatch climbing to a floor
 * while a Party Starter funds, solid green once it's on. Verb color always
 * matches bar color (see VerbButton).
 *
 * Three visual states:
 *   - ClimbBar   → Party Starter still funding. Red 45° hatch fills to
 *                  current/goal, with an "X/Y" count box on the right.
 *   - GreenLitBar→ Guaranteed, greenlit, or free. Solid green, "GREEN LIT"
 *                  label left, optional right label (e.g. "FREE" or club tag).
 */

export const HATCH = 'repeating-linear-gradient(45deg,#fe0100,#fe0100 5px,#fff 5px,#fff 10px)';

export function ClimbBar({ current = 0, goal = 0, className = '' }) {
  const pct = goal > 0 ? Math.min(100, Math.round((current / goal) * 100)) : 0;
  return (
    <div className={`flex h-6 border-b-2 border-black ${className}`}>
      <div className="relative flex-1 bg-white">
        <div
          className="absolute inset-y-0 left-0"
          style={{ width: `${pct}%`, background: HATCH }}
        />
      </div>
      <div className="flex items-center whitespace-nowrap border-l-2 border-black bg-white px-2 font-mono text-[9.5px] font-bold text-glitch-red">
        {current}/{goal}
      </div>
    </div>
  );
}

/**
 * A labeled status bar: the type/status on the left, the access mode (OPEN /
 * CURATED) in a box on the right. `bg` colours the strip — green for greenlit,
 * white for guaranteed, #FF00FF for signals.
 */
export function GreenLitBar({ label = 'Green lit', rightLabel, bg = 'bg-glitch-green', textColor = 'text-black', className = '', style, size = 'sm', rightBg = 'bg-white', rightTextColor = 'text-black' }) {
  // 'lg' is the handoff header row: 11px/700/0.14em, padding 8px 14px, no fixed
  // height. 'sm' keeps the old compact 24px strip for dense surfaces.
  const box = size === 'lg' ? 'px-3.5 py-2 text-[11px] tracking-[0.14em]' : 'px-2 text-[9.5px] tracking-wide';
  const row = size === 'lg' ? '' : 'h-6';
  return (
    <div className={`flex ${row} border-b-2 border-black ${bg} ${className}`} style={style}>
      <div className={`flex items-center whitespace-nowrap font-mono font-bold uppercase ${box} ${textColor}`}>
        {label}
      </div>
      <div className="flex-1" />
      {rightLabel && (
        <div className={`flex items-center whitespace-nowrap border-l-2 border-black font-mono font-bold uppercase ${rightBg} ${rightTextColor} ${box}`}>
          {rightLabel}
        </div>
      )}
    </div>
  );
}

/**
 * Chooser from an event's funding type + status. Every event gets a bar with the
 * access mode (OPEN / CURATED) on the right:
 *   - party_starter still funding → ClimbBar (red hatch climbing to the floor + count)
 *   - greenlit                    → green "GREEN LIT"
 *   - party_starter (not funding) → white "PARTY STARTER"
 *   - guaranteed / selling        → white "GUARANTEED"
 */
export default function EventStatusBar({
  fundingType,
  status,
  current = 0,
  goal = 0,
  isFree = false,
  mode,
  rightLabel,
  className = '',
  size = 'sm',
}) {
  const modeLabel = rightLabel ?? (mode === 'curated' ? 'Curated' : 'Open');

  // Party Starter's pledge progress now lives in a vertical bar on the card's
  // right edge (ScrollEventCard), so the top bar here is a plain label like the rest.
  const isGreenlit = status === 'greenlit';
  const isParty = !isGreenlit && fundingType === 'party_starter';
  const label = isGreenlit ? 'Green lit' : isParty ? 'Party starter' : 'Guaranteed';

  // Greenlit = green, Party Starter = magenta (#FF00FF), Guaranteed = white.
  const bg = isGreenlit ? 'bg-glitch-green' : isParty ? '' : 'bg-white';
  const style = isParty ? { background: '#FF00FF' } : undefined;
  const textColor = isParty ? 'text-white' : 'text-black';

  return <GreenLitBar label={label} bg={bg} textColor={textColor} style={style} rightLabel={modeLabel} className={className} size={size} />;
}
